/**
 * PartyWindow - 파티창 (기본 단축키 O)
 * - [대상 초대] / 이름으로 초대 / 접속 중 캐릭터 클릭 초대
 * - 초대를 받으면 화면 상단에 "OOO이(가) 파티에 초대합니다" + [수락]/[거절] 팝업
 * - 파티가 되면 파티원 리스트(이름/직업/Lv/HP), 파티장은 강퇴, 본인은 탈퇴
 */

import { EntityRegistry } from '../../core/EntityRegistry';
import { EntityState } from '../../core/Types';
import { Settings } from '../../core/Settings';
import { EventBus } from '../../core/EventBus';
import { HudLayout } from '../view/DaocHud';
import { PartyManager, PARTY_MAX } from './PartyManager';

export class PartyWindow {
  private el: HTMLElement | null = null;
  private popupEl: HTMLElement | null = null;
  private popupTimer: number | null = null;
  private isOpen = false;
  private acc = 0;
  private lastKey = '';
  private lastMessage = '';
  private nameDraft = '';

  constructor(private registry: EntityRegistry, private party: PartyManager) {}

  public mount(parent: HTMLElement): void {
    const el = document.createElement('div');
    el.id = 'party-window';
    el.style.display = 'none';
    el.style.flexDirection = 'column';
    el.style.overflow = 'hidden';
    el.style.color = '#e8dcc0';
    el.style.userSelect = 'none';
    parent.appendChild(el);
    this.el = el;
    HudLayout.wrap(el, { id: 'win-party', mode: 'free', anchor: 'tl', dx: 300, dy: 130, w: 330, h: 380, minW: 250, minH: 200 });

    const popup = document.createElement('div');
    popup.id = 'party-invite-popup';
    popup.style.cssText =
      'display:none;position:fixed;left:50%;top:96px;transform:translateX(-50%);z-index:9500;min-width:300px;' +
      'background:linear-gradient(#2b2218,#1b150e);border:2px solid #f3d98b;border-radius:6px;padding:12px 16px;' +
      'box-shadow:0 8px 30px rgba(0,0,0,.7);color:#f3e7c3;text-align:center;font-size:13px;';
    parent.appendChild(popup);
    this.popupEl = popup;

    this.party.onNotice = (text) => this.say(text, false);
    this.party.onIncomingInvite = (from) => this.showInvitePopup(from);
    this.party.onInviteClosed = () => this.hideInvitePopup();

    window.addEventListener('keydown', (e) => {
      if ((window as any).__TERRA_GAME_STATE__ && (window as any).__TERRA_GAME_STATE__() !== 'IN_WORLD') return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (Settings.actionForKey(e.key) === 'party') this.toggle();
    });
  }

  public toggle(): void {
    this.isOpen ? this.close() : this.open();
  }

  public open(): void {
    this.isOpen = true;
    if (this.el) this.el.style.display = 'flex';
    this.lastKey = '';
    this.render();
    HudLayout.focus('win-party');
  }

  public close(): void {
    this.isOpen = false;
    if (this.el) this.el.style.display = 'none';
  }

  private say(text: string, rerender = true): void {
    this.lastMessage = text;
    EventBus.emit('LOG_EMITTED', { id: Math.random().toString(), timestamp: Date.now(), text: `[파티] ${text}`, category: 'system' } as any);
    if (rerender) {
      this.lastKey = '';
      this.render();
    }
  }

  private showInvitePopup(from: string): void {
    if (!this.popupEl) return;
    this.popupEl.innerHTML = `
      <div style="font-size:11px;color:#cdb98a;margin-bottom:6px;">👥 파티 초대</div>
      <div style="font-size:14px;margin-bottom:10px;"><b style="color:#fde047;">${escapeHtml(from)}</b>${iga(from)} 파티에 초대합니다.</div>
      <div style="display:flex;gap:8px;justify-content:center;">
        <button id="pi-accept" style="${btn('#166534')}padding:6px 22px;font-size:13px;">수락</button>
        <button id="pi-decline" style="${btn('#7f1d1d')}padding:6px 22px;font-size:13px;">거절</button>
      </div>
      <div id="pi-timer" style="font-size:10px;color:#8a7a55;margin-top:6px;"></div>`;
    this.popupEl.style.display = 'block';
    this.popupEl.querySelector('#pi-accept')?.addEventListener('click', () => this.party.respondInvite(true));
    this.popupEl.querySelector('#pi-decline')?.addEventListener('click', () => this.party.respondInvite(false));
    let left = 30;
    const t = this.popupEl.querySelector('#pi-timer');
    if (t) t.textContent = `${left}초 후 자동 거절`;
    if (this.popupTimer !== null) window.clearInterval(this.popupTimer);
    this.popupTimer = window.setInterval(() => {
      left--;
      const tt = this.popupEl?.querySelector('#pi-timer');
      if (tt) tt.textContent = `${Math.max(0, left)}초 후 자동 거절`;
    }, 1000);
    this.say(`[${from}] 님이 파티에 초대했습니다.`, false);
  }

  private hideInvitePopup(): void {
    if (this.popupEl) this.popupEl.style.display = 'none';
    if (this.popupTimer !== null) {
      window.clearInterval(this.popupTimer);
      this.popupTimer = null;
    }
  }

  /** 매 프레임 호출 - 파티 상태 갱신 + 0.25초마다 변경 여부 확인 후 다시 그림 */
  public update(player: EntityState, dt: number): void {
    this.party.update(dt);
    if (!this.isOpen) return;
    this.acc += dt;
    if (this.acc < 0.25) return;
    this.acc = 0;
    const views = this.party.getMemberViews();
    const target = player.targetId ? this.registry.get(player.targetId) : null;
    const key =
      (target ? target.name : '-') +
      '|' +
      views.map((m) => `${m.name}:${m.hp}:${m.level}:${m.leader}`).join(',') +
      '|' +
      this.party.getOnlineNames().join(',') +
      '|' +
      this.lastMessage;
    if (key !== this.lastKey) {
      this.lastKey = key;
      this.render();
    }
  }

  private render(): void {
    if (!this.el) return;
    const me = this.registry.getPlayer();
    if (!me) return;
    const views = this.party.getMemberViews();
    const inParty = this.party.isInParty();
    const isLeader = this.party.isLeader();
    const target = me.targetId ? this.registry.get(me.targetId) : null;
    const memberNames = new Set(views.map((v) => v.name));
    const online = this.party.getOnlineNames().filter((n) => !memberNames.has(n));
    const canInvite = !inParty || isLeader;

    const rows = views
      .map((m) => {
        const crown = m.leader ? '👑 ' : '';
        const action = inParty && isLeader && !m.isMe ? `<button data-kick="${escapeAttr(m.name)}" style="${btn('#7f1d1d')}">강퇴</button>` : '';
        return `
          <div style="background:#0c0905;border:1px solid #3f2f17;border-radius:3px;padding:5px 7px;margin-bottom:4px;">
            <div style="display:flex;justify-content:space-between;align-items:center;font-size:12px;">
              <span><b style="color:#fff;">${crown}${escapeHtml(m.name)}</b>${m.isMe ? ' <span style="color:#8a7a55;">(나)</span>' : ''}
                <span style="color:#cdb98a;font-size:10.5px;"> ${escapeHtml(m.job)} · Lv.${m.level}</span></span>
              ${action}
            </div>
            <div style="height:7px;background:#000;margin-top:4px;border-radius:2px;overflow:hidden;">
              <div style="width:${m.hp}%;height:100%;background:linear-gradient(#e0483a,#8c1f16);"></div>
            </div>
          </div>`;
      })
      .join('');

    const onlineChips = online.length
      ? online
          .map(
            (n) =>
              `<button data-invite-name="${escapeAttr(n)}" ${canInvite ? '' : 'disabled'} style="${btn('#1d4f8f')}margin:0 4px 4px 0;${canInvite ? '' : 'opacity:.5;cursor:default;'}">${escapeHtml(n)} +</button>`
          )
          .join('')
      : '<span style="color:#8a7a55;font-size:10.5px;">접속 중인 다른 캐릭터가 없습니다.</span>';

    this.el.innerHTML = `
      <div class="daoc-title" style="flex-shrink:0;align-items:center;">
        <span>👥 파티 <span style="font-weight:normal;color:#cdb98a;">(${inParty ? views.length : 0}/${PARTY_MAX})</span></span>
        <button id="pw-close" style="background:none;border:none;color:#f3d98b;cursor:pointer;font-size:14px;line-height:1;padding:0 2px;">✕</button>
      </div>
      <div style="padding:8px;flex:1;min-height:0;overflow-y:auto;font-size:12px;">
        <div style="background:#241b10;border:1px solid #3f2f17;border-radius:3px;padding:6px 8px;margin-bottom:8px;">
          <div style="font-size:11px;color:#cdb98a;margin-bottom:5px;">선택한 대상: <b style="color:${target ? '#fde047' : '#8a7a55'};">${target ? escapeHtml(target.name) : '없음'}</b></div>
          <button id="pw-invite-target" ${canInvite ? '' : 'disabled'} style="${btn('#1d4f8f')}width:100%;padding:6px;font-size:12px;${canInvite ? '' : 'opacity:.5;'}">🎯 대상 초대</button>
          <div style="display:flex;gap:4px;margin-top:6px;">
            <input id="pw-name" type="text" maxlength="12" value="${escapeAttr(this.nameDraft)}" placeholder="캐릭터 이름으로 초대" style="flex:1;min-width:0;background:#090d16;border:1px solid #475569;color:#fff;border-radius:3px;padding:4px 6px;font-size:12px;" />
            <button id="pw-invite-name" ${canInvite ? '' : 'disabled'} style="${btn('#1d4f8f')}padding:4px 10px;${canInvite ? '' : 'opacity:.5;'}">초대</button>
          </div>
          <div style="margin-top:6px;font-size:10.5px;color:#cdb98a;">접속 중:</div>
          <div style="margin-top:3px;">${onlineChips}</div>
          ${this.lastMessage ? `<div style="margin-top:5px;font-size:10.5px;color:#fbbf24;">${escapeHtml(this.lastMessage)}</div>` : ''}
          ${!canInvite ? '<div style="margin-top:4px;font-size:10.5px;color:#f87171;">파티장만 초대할 수 있습니다.</div>' : ''}
        </div>
        ${
          inParty
            ? rows + `<button id="pw-leave" style="${btn('#7f1d1d')}width:100%;margin-top:4px;padding:5px;">파티 탈퇴</button>`
            : `<div style="color:#8a7a55;font-size:11px;line-height:1.5;padding:2px;">현재 파티가 없습니다. 초대를 보내고 상대가 수락하면 파티가 만들어집니다.</div>`
        }
      </div>`;

    const input = this.el.querySelector('#pw-name') as HTMLInputElement | null;
    input?.addEventListener('input', () => (this.nameDraft = input.value));
    input?.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') this.sendInvite(input.value);
    });
    this.el.querySelector('#pw-close')?.addEventListener('click', () => this.close());
    this.el.querySelector('#pw-invite-target')?.addEventListener('click', () => {
      const pl = this.registry.getPlayer();
      const t = pl?.targetId ? this.registry.get(pl.targetId) : null;
      if (!t) return this.say('먼저 초대할 대상을 선택(클릭/Tab)해 주세요.');
      if (t.category !== 'player') return this.say(`[${t.name}]은(는) 플레이어가 아니므로 초대할 수 없습니다.`);
      this.sendInvite(t.name);
    });
    this.el.querySelector('#pw-invite-name')?.addEventListener('click', () => this.sendInvite(input?.value ?? ''));
    this.el.querySelectorAll<HTMLElement>('[data-invite-name]').forEach((b) => b.addEventListener('click', () => this.sendInvite(b.dataset.inviteName!)));
    this.el.querySelector('#pw-leave')?.addEventListener('click', () => this.say(this.party.leave().message));
    this.el.querySelectorAll<HTMLElement>('[data-kick]').forEach((b) => b.addEventListener('click', () => this.say(this.party.kick(b.dataset.kick!).message)));
  }

  private sendInvite(name: string): void {
    this.say(this.party.invite(name).message);
  }
}

function btn(bg: string): string {
  return `background:${bg};border:1px solid #f3d98b66;color:#fff;border-radius:3px;cursor:pointer;font-size:11px;padding:2px 8px;`;
}
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
function escapeAttr(s: string): string {
  return escapeHtml(s);
}

/** 한글 받침 유무에 따라 이/가 선택 (한글이 아니면 이(가)) */
function iga(name: string): string {
  const ch = name.charCodeAt(name.length - 1);
  if (ch >= 0xac00 && ch <= 0xd7a3) return (ch - 0xac00) % 28 === 0 ? '가' : '이';
  return '이(가)';
}
